import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import { fetchPublicDemands } from '@/service/api/demand';
import { fetchPublicJobPostings } from '@/service/api/job';
import { formatJobSalaryDisplay, employmentTypeMap } from '@/pages/jobs/detail/page';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import { useAuth } from '@/hooks/useAuth';
import ConfirmModal from '@/components/common/ConfirmModal';
import CityCascader from '@/components/common/CityCascader';
import DatePicker from '@/components/common/DatePicker';
import BannerCarousel from '@/components/common/BannerCarousel';
import FilterTabs from '@/components/common/FilterTabs';
import Pagination from '@/components/common/Pagination';
import './responsive.css';

const ITEMS_PER_PAGE = 12;

/** 格式化日期为 YYYY-MM-DD */
function formatDateShort(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** 格式化预算展示文本 */
function formatBudgetDisplay(budgetDisplay?: string, budgetType?: string): string {
  if (!budgetDisplay || budgetDisplay.trim() === '') {
    return budgetType === 'NEGOTIABLE' ? '面议' : '面议';
  }
  if (budgetDisplay.includes('面议') || budgetType === 'NEGOTIABLE') {
    return '面议';
  }

  const rangeMatch = budgetDisplay.match(/^(\d+)\s*-\s*(\d+)\s*(CNY|[A-Z]{3})?$/i);
  if (rangeMatch) {
    const num1 = parseInt(rangeMatch[1], 10);
    const num2 = parseInt(rangeMatch[2], 10);
    const str1 = num1 >= 10000 ? `${(num1 / 10000).toFixed(num1 % 10000 === 0 ? 0 : 1)}万` : `${num1}`;
    const str2 = num2 >= 10000 ? `${(num2 / 10000).toFixed(num2 % 10000 === 0 ? 0 : 1)}万` : `${num2}`;
    return `${str1} - ${str2}元`;
  }

  const singleMatch = budgetDisplay.match(/^(\d+)\s*(CNY|[A-Z]{3})?$/i);
  if (singleMatch) {
    const num = parseInt(singleMatch[1], 10);
    const str = num >= 10000 ? `${(num / 10000).toFixed(num % 10000 === 0 ? 0 : 1)}万` : `${num}`;
    return `${str}元`;
  }

  return budgetDisplay.replace(/\bCNY\b/gi, '元').trim();
}

/** 针对需求分类计算卡片浅色调样式 */
function getCategoryThemeStyle(catName?: string) {
  if (!catName) return { bg: 'bg-emerald-50/40 border-emerald-100/60', badge: 'bg-emerald-50 text-emerald-600' };
  if (catName.includes('企业') || catName.includes('宣传片')) {
    return { bg: 'bg-purple-50/40 border-purple-100/60', badge: 'bg-purple-50 text-purple-600 border border-purple-200/50' };
  }
  if (catName.includes('短剧')) {
    return { bg: 'bg-emerald-50/40 border-emerald-100/60', badge: 'bg-emerald-50 text-emerald-600 border border-emerald-200/50' };
  }
  if (catName.includes('漫剧') || catName.includes('动画')) {
    return { bg: 'bg-blue-50/40 border-blue-100/60', badge: 'bg-blue-50 text-blue-600 border border-blue-200/50' };
  }
  if (catName.includes('产品')) {
    return { bg: 'bg-amber-50/40 border-amber-100/60', badge: 'bg-amber-50 text-amber-600 border border-amber-200/50' };
  }
  return { bg: 'bg-emerald-50/40 border-emerald-100/60', badge: 'bg-emerald-50 text-emerald-600 border border-emerald-200/50' };
}

/** 状态样式与文案 */
const statusConfigMap: Record<string, { label: string; style: string }> = {
  RECRUITING: { label: '招募中', style: 'bg-emerald-50 text-emerald-600 border border-emerald-200/60' },
  open: { label: '招募中', style: 'bg-emerald-50 text-emerald-600 border border-emerald-200/60' },
  CLOSED: { label: '已截止', style: 'bg-gray-100 text-gray-400 border border-gray-200/60' },
  closed: { label: '已截止', style: 'bg-gray-100 text-gray-400 border border-gray-200/60' },
  FULL: { label: '已招满', style: 'bg-gray-100 text-gray-500 border border-gray-200/60' },
  COMPLETED: { label: '已完成', style: 'bg-blue-50 text-blue-600 border border-blue-200/60' },
  awarded: { label: '已中标', style: 'bg-blue-50 text-blue-600 border border-blue-200/60' },
};

export default function DemandsPage() {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();
  const isEnterpriseVerified = user?.accountType === 'ENTERPRISE' && user?.verified;
  const [showCertModal, setShowCertModal] = useState(false);

  const handlePostDemandClick = () => {
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    if (!isEnterpriseVerified) {
      setShowCertModal(true);
      return;
    }
    navigate(activeTab === 'demands' ? '/post-demand' : '/post-job');
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const tabInUrl = searchParams.get('tab');
  const activeTab: 'demands' | 'jobs' =
    tabInUrl === 'jobs' || tabInUrl === 'demands'
      ? tabInUrl
      : (localStorage.getItem('demands_active_tab') as 'demands' | 'jobs') || 'demands';

  const [selectedCategoryCode, setSelectedCategoryCode] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('latest');
  const [currentPage, setCurrentPage] = useState(1);

  // 需求与招聘列表状态
  const [demands, setDemands] = useState<Api.Demand.DemandResponse[]>([]);
  const [jobs, setJobs] = useState<Api.Job.JobResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isInitial, setIsInitial] = useState(true);

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();

  const categoriesFetchedRef = useRef(false);
  const lastFetchParamsRef = useRef<string | null>(null);

  // 监听 Tab 切换重置页面与缓存
  const prevTabRef = useRef<'demands' | 'jobs'>(activeTab);
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      prevTabRef.current = activeTab;
      setCurrentPage(1);
      lastFetchParamsRef.current = null;
      localStorage.setItem('demands_active_tab', activeTab);
    }
  }, [activeTab]);

  // 初始化获取分类数据
  useEffect(() => {
    if (categoriesFetchedRef.current || categories.length > 0) return;
    categoriesFetchedRef.current = true;

    fetchCategories().catch((err) => {
      console.error('获取业务类目失败:', err);
      categoriesFetchedRef.current = false;
    });
  }, [categories.length, fetchCategories]);

  // 加载需求或招聘岗位列表数据
  const loadData = useCallback(async (force = false) => {
    const queryParams = {
      category_code: selectedCategoryCode || undefined,
      page: currentPage,
      page_size: ITEMS_PER_PAGE,
    };
    const paramsKey = JSON.stringify({ activeTab, ...queryParams, statusFilter, cityFilter, dateFilter, sortOrder });

    if (!force && lastFetchParamsRef.current === paramsKey) {
      return;
    }
    lastFetchParamsRef.current = paramsKey;

    setLoading(true);
    try {
      if (activeTab === 'demands') {
        const res = await fetchPublicDemands(queryParams);
        let list = res?.items || [];
        // 前端补充状态和城市过滤逻辑
        if (statusFilter !== 'ALL') {
          list = list.filter((item) => {
            const st = (item.status || item.bid_status || '').toUpperCase();
            if (statusFilter === 'RECRUITING') return st === 'RECRUITING' || st === 'OPEN';
            if (statusFilter === 'CLOSED') return st === 'CLOSED';
            if (statusFilter === 'FULL') return st === 'FULL' || st === 'COMPLETED';
            return true;
          });
        }
        setDemands(list);
        setTotal(res?.total || list.length);
      } else {
        const res = await fetchPublicJobPostings(queryParams);
        let list = res?.items || [];
        if (statusFilter !== 'ALL') {
          list = list.filter((item) => {
            const st = (item.status || '').toUpperCase();
            if (statusFilter === 'RECRUITING') return st === 'RECRUITING' || st === 'PUBLISHED';
            if (statusFilter === 'CLOSED') return st === 'CLOSED';
            return true;
          });
        }
        setJobs(list);
        setTotal(res?.total || list.length);
      }
    } catch (err) {
      console.error(activeTab === 'demands' ? '获取需求列表失败:' : '获取招聘岗位失败:', err);
      if (activeTab === 'demands') setDemands([]);
      else setJobs([]);
      setTotal(0);
      lastFetchParamsRef.current = null;
    } finally {
      setLoading(false);
      setIsInitial(false);
    }
  }, [activeTab, selectedCategoryCode, currentPage, statusFilter, cityFilter, dateFilter, sortOrder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Tab 切换处理
  const handleTabChange = (tab: 'demands' | 'jobs') => {
    if (tab === activeTab) return;
    localStorage.setItem('demands_active_tab', tab);
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.set('tab', tab);
      return updated;
    });
  };

  // 选择分类
  const handleCategorySelect = (code: string) => {
    setSelectedCategoryCode(code);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

  return (
    <div className="demands-page min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* 顶部 Header / Hero Banner */}
      <div className="pt-14 sm:pt-16 pb-6 md:pb-8">
        {/* 顶部全宽 Banner 轮播图：直接无缝贴合 Navbar 铺满屏幕 */}
        <div className="w-full mb-6 md:mb-8">
          <BannerCarousel
            type="demands"
            height="96px"
            hoverScale={false}
            rounded={false}
            className="w-full rounded-none overflow-hidden"
          />
        </div>

        <div className="site-shell max-w-7xl mx-auto px-4 md:px-8">
          {/* 居中标题 */}
          <div className="text-center mb-6 md:mb-8">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              谋势供需服务
            </h1>
          </div>

          {/* 顶部 Hero 控制栏：左侧Tab + 中间分类Pills + 右侧发布按钮 */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 w-full">

            {/* 左侧：需求大厅 / 招聘岗位 Tab 切换器 (带有滑块平移动画) */}
            <div className="relative inline-flex items-center p-1 bg-[#f3f4f6] rounded-xl shrink-0 select-none">
              {/* 平滑滑块背景 */}
              <div
                className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] bg-white rounded-lg shadow-2xs transition-transform duration-300 ease-out"
                style={{
                  transform: activeTab === 'jobs' ? 'translateX(100%)' : 'translateX(0%)',
                }}
              />
              <button
                type="button"
                onClick={() => handleTabChange('demands')}
                className={`relative z-10 py-1.5 px-4 text-xs md:text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer ${activeTab === 'demands'
                  ? 'text-[#10b981]'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                需求大厅
              </button>
              <button
                type="button"
                onClick={() => handleTabChange('jobs')}
                className={`relative z-10 py-1.5 px-4 text-xs md:text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer ${activeTab === 'jobs'
                  ? 'text-[#10b981]'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                招聘岗位
              </button>
            </div>

            {/* 中间：业务分类 FilterTabs (宽度铺满并居中) */}
            <FilterTabs
              className="w-full flex-1 min-w-0"
              align="center"
              options={categories.map((cat) => ({
                label: cat.name,
                value: cat.code || cat.id || '',
              }))}
              value={selectedCategoryCode}
              onChange={(val) => handleCategorySelect(val)}
              allLabel={activeTab === 'demands' ? '全部需求' : '全部岗位'}
              allValue=""
              popoverTitle={activeTab === 'demands' ? '全部分类' : '全部分类'}
              itemClassName="px-4 py-1.5 rounded-full text-sm md:text-base font-medium transition-all cursor-pointer whitespace-nowrap shrink-0 select-none"
              activeItemClassName="bg-[#10b981] text-white shadow-2xs"
              inactiveItemClassName="bg-gray-100/90 text-gray-700 hover:bg-gray-200/80"
            />

            {/* 右侧：发布按钮 */}
            <button
              type="button"
              onClick={handlePostDemandClick}
              className="inline-flex items-center justify-center gap-1.5 bg-[#10b981] hover:bg-[#059669] active:bg-[#047857] text-white font-medium text-xs md:text-sm px-5 py-2 rounded-lg transition-all cursor-pointer shrink-0 whitespace-nowrap shadow-2xs"
            >
              <i className="ri-add-line text-sm"></i>
              <span>{activeTab === 'demands' ? '发布需求' : '发布招聘'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 页面主主体：单列布局 */}
      <div className="site-shell max-w-7xl mx-auto px-4 md:px-8 pb-10 w-full flex-1 flex flex-col">
        <main className="w-full flex-1 flex flex-col gap-4">

          {/* 卡片 Grid 列表 */}
          <div className="relative min-h-[400px]">
            {isInitial && loading ? (
              /* Skeleton 骨架屏（仅全屏冷启动时展示） */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse flex flex-col justify-between h-52">
                    <div>
                      <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : !loading && (activeTab === 'demands' ? demands.length === 0 : jobs.length === 0) ? (
              /* 空数据（确保非 loading 状态且确实无数据时展示） */
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-xs animate-tab-content">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-50 flex items-center justify-center">
                  <i className="ri-inbox-line text-3xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 text-sm">
                  {activeTab === 'demands' ? '暂无匹配的需求数据' : '暂无匹配的招聘岗位'}
                </p>
                {(selectedCategoryCode || statusFilter !== 'ALL' || cityFilter || dateFilter) && (
                  <button
                    onClick={() => {
                      setSelectedCategoryCode('');
                      setStatusFilter('ALL');
                      setCityFilter('');
                      setDateFilter('');
                      setCurrentPage(1);
                    }}
                    className="mt-3 text-xs text-emerald-600 hover:underline cursor-pointer font-medium"
                  >
                    重置筛选条件
                  </button>
                )}
              </div>
            ) : (
              /* 真实数据卡片列表（Tab/分类/页码切换时平滑淡化与淡入，绝无骨架屏闪烁） */
              <div
                key={`${activeTab}-${currentPage}-${selectedCategoryCode}`}
                className={`grid grid-cols-1 md:grid-cols-2 gap-5 transition-all duration-300 ${
                  loading ? 'opacity-40 pointer-events-none scale-[0.995]' : 'opacity-100 scale-100 animate-tab-content'
                }`}
              >
                {activeTab === 'demands'
                  ? demands.map((demand) => (
                    <DemandGridCard
                      key={demand.id}
                      demand={demand}
                      getCategoryNameByCode={getCategoryNameByCode}
                    />
                  ))
                  : jobs.map((job) => (
                    <JobGridCard
                      key={job.id}
                      job={job}
                      getCategoryNameByCode={getCategoryNameByCode}
                    />
                  ))}
              </div>
            )}
          </div>

          {/* 底部分页器 */}
          {!isInitial && total > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              loading={loading}
              className="pt-6 py-3"
            />
          )}

        </main>
      </div>

      <ConfirmModal
        open={showCertModal}
        type="warning"
        title="需要完成企业认证"
        content="发布需求与发布招聘功能仅面向通过认证的企业账号开放。请先完成企业认证。"
        confirmText="前往企业认证"
        cancelText="取消"
        onConfirm={() => {
          setShowCertModal(false);
          navigate('/certification');
        }}
        onCancel={() => setShowCertModal(false)}
      />
    </div>
  );
}

/** 参考站酷 opportunity-item-card 样式的需求卡片组件 */
function DemandGridCard({
  demand,
  getCategoryNameByCode,
}: {
  demand: Api.Demand.DemandResponse;
  getCategoryNameByCode: (code?: string, fallback?: string) => string;
}) {
  const categoryNames =
    demand.category_codes && demand.category_codes.length > 0
      ? demand.category_codes.map((code) => getCategoryNameByCode(code, code))
      : ['需求'];

  const mainCategory = categoryNames[0] || '需求';
  const statusKey = demand.status || demand.bid_status || 'RECRUITING';
  const statusConfig = statusConfigMap[statusKey] || { label: '招募中', style: 'bg-emerald-50 text-emerald-600 border border-emerald-200/60' };

  const budgetDisplay = formatBudgetDisplay(demand.budget_display, demand.budget_type);

  const deadlineText = demand.bid_deadline_at
    ? formatDateShort(demand.bid_deadline_at)
    : demand.expected_delivery_at
      ? formatDateShort(demand.expected_delivery_at)
      : '长期有效';

  return (
    <Link
      to={`/demands/${demand.id}`}
      className="group bg-white hover:bg-[#fafafa] rounded-2xl border border-gray-100/90 hover:border-gray-200/90 transition-all duration-200 p-5 flex flex-col justify-between shadow-2xs hover:shadow-md cursor-pointer relative overflow-hidden"
    >
      <div>
        {/* 1. 顶部发布企业名称与发布时间 */}
        <div className="flex items-center justify-between text-xs text-gray-400 font-medium mb-2">
          <span className="truncate max-w-[200px] hover:text-gray-600 transition-colors">
            {demand.enterprise_name_masked || '谋势合作方'}
          </span>
          <span className="shrink-0 text-[11px]">截止 {deadlineText}</span>
        </div>

        {/* 2. 项目标题 */}
        <h3 className="font-bold text-foreground-950 text-base sm:text-lg leading-snug group-hover:text-[#10b981] transition-colors line-clamp-1 mb-2.5">
          {demand.title}
        </h3>

        {/* 3. 标签与元信息行 (分类 Tag, 状态 Badge, 城市) */}
        <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
          <span className="bg-emerald-50 text-emerald-700 text-xs px-2.5 py-0.5 rounded-md font-medium border border-emerald-100/80">
            {mainCategory}
          </span>
          <span className={`px-2.5 py-0.5 rounded-md font-medium text-xs ${statusConfig.style}`}>
            {statusConfig.label}
          </span>
          {/* <span className="text-gray-400 text-xs flex items-center gap-1 ml-auto shrink-0">
            <i className="ri-map-pin-line text-gray-400"></i>
            {demand.city_name || '全国'}
          </span> */}
        </div>

        {/* 4. 描述内容摘要 */}
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-4 min-h-[36px]">
          {demand.description_summary || demand.description || '暂无详细需求说明...'}
        </p>
      </div>

      {/* 5. 底部两端对齐 (发布者信息 + 预算价格) */}
      <div className="border-t border-gray-100/80 pt-3 flex items-center justify-between mt-auto">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">
            {(demand.enterprise_name_masked || '谋')[0]}
          </div>
          <span className="text-xs text-gray-600 truncate font-medium max-w-[120px]">
            {demand.enterprise_name_masked || '谋势客户'}
          </span>
        </div>

        <div className="text-right shrink-0">
          <span className="text-[#ff3366] font-bold text-base sm:text-lg">
            {budgetDisplay}
          </span>
        </div>
      </div>
    </Link>
  );
}

/** 参考站酷 opportunity-item-card 样式的招聘岗位卡片组件 */
function JobGridCard({
  job,
  getCategoryNameByCode,
}: {
  job: Api.Job.JobResponse;
  getCategoryNameByCode: (code?: string, fallback?: string) => string;
}) {
  const categoryNames =
    job.category_codes && job.category_codes.length > 0
      ? job.category_codes.map((code) => getCategoryNameByCode(code, code))
      : ['招聘'];

  const mainCategory = categoryNames[0] || '招聘';

  const salaryText = formatJobSalaryDisplay(
    job.salary_min,
    job.salary_max,
    job.salary_type,
    job.salary_period,
    job.salary_currency
  );

  const statusKey = job.status || 'RECRUITING';
  const statusConfig = statusConfigMap[statusKey] || { label: '招聘中', style: 'bg-emerald-50 text-emerald-600 border border-emerald-200/60' };
  const empTypeLabel = employmentTypeMap[job.employment_type || ''] || job.employment_type || '全职';

  return (
    <Link
      to={`/jobs/${job.id}`}
      className="group bg-white hover:bg-[#fafafa] rounded-2xl border border-gray-100/90 hover:border-gray-200/90 transition-all duration-200 p-5 flex flex-col justify-between shadow-2xs hover:shadow-md cursor-pointer relative overflow-hidden"
    >
      <div>
        {/* 1. 顶部发布企业名称与性质 */}
        <div className="flex items-center justify-between text-xs text-gray-400 font-medium mb-2">
          <span className="truncate max-w-[200px] hover:text-gray-600 transition-colors">
            {job.enterprise_name_masked || '谋势合作品牌'}
          </span>
          <span className="shrink-0 text-[11px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-medium">
            {empTypeLabel}
          </span>
        </div>

        {/* 2. 岗位标题 */}
        <h3 className="font-bold text-foreground-950 text-base sm:text-lg leading-snug group-hover:text-[#10b981] transition-colors line-clamp-1 mb-2.5">
          {job.title}
        </h3>

        {/* 3. 标签与元信息行 */}
        <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
          <span className="bg-emerald-50 text-emerald-700 text-xs px-2.5 py-0.5 rounded-md font-medium border border-emerald-100/80">
            {mainCategory}
          </span>
          <span className={`px-2.5 py-0.5 rounded-md font-medium text-xs ${statusConfig.style}`}>
            {statusConfig.label}
          </span>
          <span className="text-gray-400 text-xs flex items-center gap-1 ml-auto shrink-0">
            <i className="ri-map-pin-line text-gray-400"></i>
            {job.city_name || '北京市'}
          </span>
        </div>

        {/* 4. 岗位描述摘要 */}
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-4 min-h-[36px]">
          {job.job_description_summary || job.job_description || '暂无详细岗位描述...'}
        </p>
      </div>

      {/* 5. 底部两端对齐 (招聘方 + 薪资) */}
      <div className="border-t border-gray-100/80 pt-3 flex items-center justify-between mt-auto">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
            {(job.enterprise_name_masked || '企')[0]}
          </div>
          <span className="text-xs text-gray-600 truncate font-medium max-w-[120px]">
            {job.enterprise_name_masked || '谋势招聘方'}
          </span>
        </div>

        <div className="text-right shrink-0">
          <span className="text-[#ff3366] font-bold text-base sm:text-lg">
            {salaryText}
          </span>
        </div>
      </div>
    </Link>
  );
}
